import { useEffect, useState } from 'react';
import type { CatchUpRow } from '../api';
import * as api from '../api';

// "Catch up from GitHub" (P5-22, Pro), at the top of Triage. Once, after the
// first backfill, a banner offers it; Settings › GitHub opens it any time
// (/review?catchup=1). Each row is a status fix the code already shows, with
// its evidence. Nothing changes until Apply; Undo puts the old statuses back.

const STATUS_LABEL: Record<string, string> = {
  captured: 'Captured', exploring: 'Exploring', building: 'Building', shipped: 'Shipped', shelved: 'Shelved',
};

function shortDate(sql: string): string {
  return new Date(`${sql.replace(' ', 'T')}Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function evidenceText(r: CatchUpRow): string {
  const { kind, at } = r.evidence;
  if (kind === 'pr_merged') return `merged${at ? ` ${shortDate(at)}` : ''}`;
  if (kind === 'pr_open') return `open since ${at ? shortDate(at) : 'recently'}`;
  return at ? `no commits since ${shortDate(at)}` : 'no recent commits';
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// Read without removing: Triage can mount twice while the library loads. The
// param leaves the URL when the list is closed or applied.
function openParam(): boolean {
  return new URLSearchParams(window.location.search).get('catchup') === '1';
}

function dropOpenParam(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('catchup')) return;
  params.delete('catchup');
  const query = params.toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
}

export function CatchUp({ onSelect, onChanged }: { onSelect: (id: number) => void; onChanged: () => void }) {
  const [rows, setRows] = useState<CatchUpRow[] | null>(null);
  const [banner, setBanner] = useState(false);
  const [open, setOpen] = useState(openParam);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<CatchUpRow[] | null>(null);
  const [undone, setUndone] = useState(false);

  useEffect(() => {
    let live = true;
    api.getGithubCatchUp()
      .then((r) => {
        if (!live || !r.pro) return;
        setRows(r.rows);
        setBanner(r.show_banner);
        setPicked(new Set(r.rows.map((row) => row.idea_id)));
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const markSeen = () => {
    setBanner(false);
    void api.patchSettings({ github_catch_up_seen: true }).catch(() => {});
  };

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.applyGithubCatchUp([...picked]);
      setApplied(r.applied);
      dropOpenParam();
      setRows((prev) => (prev ?? []).filter((row) => !r.applied.some((a) => a.idea_id === row.idea_id)));
      setOpen(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not apply');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!applied) return;
    setBusy(true);
    setError(null);
    try {
      for (const a of applied) await api.updateIdea(a.idea_id, { status: a.from });
      setUndone(true);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not undo');
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (rows === null) return null;

  if (applied) {
    return (
      <section className="tri-card catchup" aria-labelledby="tri-catchup" role="status">
        <h2 className="tri-h2" id="tri-catchup">Catch up from GitHub</h2>
        <p className="tri-help">
          {undone
            ? `Undone. ${plural(applied.length, 'idea')} back to where ${applied.length === 1 ? 'it was' : 'they were'}.`
            : applied.length === 0
              ? 'Nothing changed: those ideas had moved on already.'
              : `Updated ${plural(applied.length, 'idea')}: ${applied.map((a) => `#${a.number} → ${STATUS_LABEL[a.to]}`).join(', ')}.`}
        </p>
        {error && <p className="tri-error" role="alert">{error}</p>}
        <div className="tri-actions">
          {!undone && applied.length > 0 && (
            <button className="fr-btn tri-sm" disabled={busy} onClick={() => void undo()}>Undo</button>
          )}
          <button className="tri-link" disabled={busy} onClick={() => { setApplied(null); setUndone(false); }}>Close</button>
        </div>
      </section>
    );
  }

  if (!open) {
    if (!banner || rows.length === 0) return null;
    return (
      <section className="tri-card catchup" aria-labelledby="tri-catchup">
        <h2 className="tri-h2" id="tri-catchup">Catch up from GitHub <span className="tri-count">{rows.length}</span></h2>
        <p className="tri-help">
          Your repos say {plural(rows.length, 'idea')} {rows.length === 1 ? 'has' : 'have'} moved on: merged pull requests,
          open ones, or work that went quiet. Review the changes and apply them in one go.
        </p>
        <div className="tri-actions">
          <button className="fr-btn fr-btn-primary tri-sm" onClick={() => { markSeen(); setOpen(true); }}>
            Review {plural(rows.length, 'change')}
          </button>
          <button className="tri-link" onClick={markSeen}>Not now</button>
        </div>
      </section>
    );
  }

  const close = () => { if (banner) markSeen(); dropOpenParam(); setOpen(false); };

  return (
    <section className="tri-section catchup" aria-labelledby="tri-catchup">
      <h2 className="tri-h2" id="tri-catchup">Catch up from GitHub {rows.length > 0 && <span className="tri-count">{rows.length}</span>}</h2>
      {rows.length === 0 ? (
        <>
          <p className="tri-help">Nothing to catch up: your ideas' statuses match what your repos show.</p>
          <div className="tri-actions"><button className="tri-link" onClick={close}>Close</button></div>
        </>
      ) : (
        <>
          <p className="tri-help">
            Status changes your code already shows. Untick any you disagree with; nothing changes until you apply.
          </p>
          <div className="tri-actions">
            <button className="tri-link" disabled={busy} onClick={() => setPicked(new Set(rows.map((r) => r.idea_id)))}>Select all</button>
            <button className="tri-link" disabled={busy} onClick={() => setPicked(new Set())}>Select none</button>
          </div>
          <div className="tri-rows">
            {rows.map((r) => {
              const id = `catchup-${r.idea_id}`;
              return (
                <div key={r.idea_id} className="tri-row catchup-row">
                  <input
                    id={id}
                    type="checkbox"
                    className="catchup-check"
                    checked={picked.has(r.idea_id)}
                    disabled={busy}
                    onChange={() => toggle(r.idea_id)}
                    aria-label={`#${r.number} ${r.title}: ${STATUS_LABEL[r.from]} to ${STATUS_LABEL[r.to]}`}
                  />
                  <button className="tri-row-main" onClick={() => onSelect(r.idea_id)}>
                    <span className="tri-row-title"><span className="tri-num">#{r.number}</span>{r.title}</span>
                    <span className="tri-meta">
                      <span className="tri-recur">{STATUS_LABEL[r.from]} → {STATUS_LABEL[r.to]}</span>
                      · {r.evidence.kind === 'stalled' ? r.evidence.label : `PR ${r.evidence.label}`} · {evidenceText(r)}
                    </span>
                  </button>
                  {r.evidence.url && (
                    <span className="tri-row-actions">
                      <a className="tri-link" href={r.evidence.url} target="_blank" rel="noopener noreferrer">View on GitHub</a>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {error && <p className="tri-error" role="alert">{error}</p>}
          <div className="tri-actions">
            <button className="fr-btn fr-btn-primary" disabled={busy || picked.size === 0} onClick={() => void apply()}>
              {picked.size === 0 ? 'Apply' : `Apply ${plural(picked.size, 'change')}`}
            </button>
            <button className="tri-link" disabled={busy} onClick={close}>Not now</button>
          </div>
        </>
      )}
    </section>
  );
}
